import { Injectable, inject } from '@angular/core';
import { BehaviorSubject, Observable } from 'rxjs';
import { SessionService } from '@core/services/session.service';
import { StorageKey } from '@core/config/global.config';

export type Theme = 'light' | 'dark';

// Must match main.ts's providePrimeNG({ theme: { options: { darkModeSelector: '.app-dark' } } }) —
// PrimeNG's own light/dark design tokens (see custom_theme.ts colorScheme.light/dark) activate
// off this same class, so our custom CSS variables (styles.scss :root / .app-dark) and PrimeNG's
// generated tokens always switch together.
const DARK_CLASS = 'app-dark';

/**
 * Persisted light/dark theme preference — mirrors the density/language pattern
 * (SessionService + apply-globally-on-change). Defaults to light the first time
 * (no stored choice yet), then respects whatever the user picks afterward.
 */
@Injectable({
  providedIn: 'root',
})
export class ThemeService {
  private sessionService = inject(SessionService);

  private theme$: BehaviorSubject<Theme>;

  constructor() {
    const initial = this.readStoredTheme();
    this.theme$ = new BehaviorSubject<Theme>(initial);
    this.apply(initial);
  }

  getTheme(): Observable<Theme> {
    return this.theme$.asObservable();
  }

  setTheme(theme: Theme): void {
    this.sessionService.setItem(StorageKey.theme, theme);
    this.apply(theme);
    this.theme$.next(theme);
  }

  private readStoredTheme(): Theme {
    const stored = this.sessionService.getItem(StorageKey.theme);
    if (stored === 'light' || stored === 'dark') {
      return stored;
    }
    return 'light';
  }

  private apply(theme: Theme): void {
    document.documentElement.classList.toggle(DARK_CLASS, theme === 'dark');
  }
}
